/* RESPONSIBLE TEAM: team-actions */

import { type EntityType } from 'embercom/models/data/entity-types';
import { KNOWLEDGE_HUB_ENTITIES_NAMES } from 'embercom/lib/knowledge-hub/constants';

interface ReasonedStep {
  section_name: string;
  step_number: string;
}

export interface NormalizedReasonedStep {
  sectionName: string;
  stepNumber: string;
}

export interface StepReasoningResult {
  i18nKeySuffix: string;
  stepNumber: string;
  subProcedure: string | undefined;
}

export const formatStepId = (stepId: string | undefined) => {
  if (!stepId) {
    return '';
  }
  // answerbot uses main_section for the main procedure in the step number for procedures
  if (stepId?.includes('main_section')) {
    // Extract step number after "main_section."
    return stepId.replace('main_section.', '');
  } else if (stepId?.includes('.')) {
    // If there's a dot and it's not main_section, extract the step number after the dot
    let firstDotIndex = stepId.indexOf('.');
    return stepId.substring(firstDotIndex + 1);
  }
  return stepId;
};

export const getSubProcedure = (
  sectionName: string | undefined,
  stepNumber: string | undefined = undefined,
) => {
  if (sectionName === 'Main Section') {
    return undefined;
  } else if (sectionName) {
    return sectionName;
  }
  if (!stepNumber) {
    return undefined;
  }
  let isSubProcedure = !stepNumber?.includes('main_section') && stepNumber?.includes('.');
  if (isSubProcedure) {
    return stepNumber?.substring(0, stepNumber?.indexOf('.'));
  }
  return undefined;
};

export const joinStepNumbers = (items: string[]) =>
  items.length < 3 ? items.join(' & ') : `${items.slice(0, -1).join(', ')} & ${items.at(-1)}`;

/**
 * Core branching logic for step reasoning events.
 * Determines the i18n key suffix and formatted step number based on
 * reasoned steps, main section presence, and sub-procedure presence.
 *
 * Returns an i18nKeySuffix (e.g. 'ai_agent_task_step_reasoning') that
 * the caller prefixes with their own i18n namespace.
 */
export const resolveStepReasoning = (
  reasonedSteps: NormalizedReasonedStep[],
  fallbackStepNumber: string | undefined,
): StepReasoningResult | undefined => {
  let hasMultipleReasonedSteps =
    reasonedSteps.length > 1 &&
    reasonedSteps.every(
      (item) => typeof item.stepNumber === 'string' && typeof item.sectionName === 'string',
    );

  // BASIC CASE: Single step reasoning
  if (!hasMultipleReasonedSteps) {
    let first = reasonedSteps[0];
    let stepNumber = first?.stepNumber ?? formatStepId(fallbackStepNumber);
    let subProcedure = getSubProcedure(first?.sectionName, fallbackStepNumber);

    return {
      i18nKeySuffix: subProcedure
        ? 'ai_agent_task_step_reasoning_sub_procedure'
        : 'ai_agent_task_step_reasoning',
      stepNumber,
      subProcedure: subProcedure || undefined,
    };
  }

  let hasMainSection = reasonedSteps.some((s) => s.sectionName === 'Main Section');
  let hasSubProcedure = reasonedSteps.some((s) => getSubProcedure(s.sectionName) !== undefined);

  // COMPLEX CASE: Main section + sub procedure
  if (hasMainSection && hasSubProcedure) {
    let stepNumbers = reasonedSteps.map((step) =>
      step.sectionName === 'Main Section'
        ? step.stepNumber
        : `${getSubProcedure(step.sectionName)} ${step.stepNumber}`,
    );
    return {
      i18nKeySuffix: 'ai_agent_task_step_reasoning_plural',
      stepNumber: joinStepNumbers(stepNumbers),
      subProcedure: undefined,
    };
  }

  let formattedNumbers = joinStepNumbers(reasonedSteps.map((s) => s.stepNumber));

  // STANDARD CASE: Multiple reasoned steps (main)
  if (hasMainSection) {
    return {
      i18nKeySuffix: 'ai_agent_task_step_reasoning_plural',
      stepNumber: formattedNumbers,
      subProcedure: undefined,
    };
  }

  // STANDARD CASE: Multiple reasoned steps (subproc)
  if (hasSubProcedure) {
    let firstSubProcedure = reasonedSteps.find(
      (s) => s.sectionName !== 'Main Section',
    )?.sectionName;

    return {
      i18nKeySuffix: 'ai_agent_task_step_reasoning_sub_procedure_plural',
      stepNumber: formattedNumbers,
      subProcedure: firstSubProcedure,
    };
  }

  return undefined;
};

/**
 * Generates a knowledge hub content URL for a given entity.
 */
export const generateContentUrl = (
  entityTypeId: number,
  entityId: number,
  appId: string | undefined,
  urlFor: (
    routeName: string,
    appId: string,
    options: { queryParams: Record<string, unknown> },
  ) => string,
): string => {
  let entityType = entityTypeId as EntityType;
  let entityTypeName =
    KNOWLEDGE_HUB_ENTITIES_NAMES[entityType as keyof typeof KNOWLEDGE_HUB_ENTITIES_NAMES];

  if (!entityTypeName || !appId) {
    return '#';
  }

  return urlFor('apps.app.automation.fin-ai-agent.content', appId, {
    queryParams: {
      activeContentId: entityId,
      activeContentType: entityTypeName,
      editorMode: 'view',
      native_content: true,
    },
  });
};

/**
 * Builds a full step reasoning event object for operator-workflow-event.
 * This is the original function used by the operator-workflow rendering path.
 *
 * Follow-up: this could be simplified by refactoring it to normalize its
 * snake_case ReasonedStep[] input and delegate to resolveStepReasoning(),
 * the same way ai-agent-event.ts does. That would remove the duplicated
 * branching logic below and reduce this file significantly.
 */
export const buildStepReasoningEvent = (
  operatorName: string,
  stepReasoning: any,
): any | undefined => {
  let reasonedSteps: ReasonedStep[] = stepReasoning?.reasoned_step_ids ?? [];
  let hasMultipleReasonedSteps =
    reasonedSteps.length > 1 &&
    reasonedSteps.every(
      (item) => typeof item.step_number === 'string' && typeof item.section_name === 'string',
    );

  let baseEvent = (overrides = {}) => ({
    operatorName,
    step_description: stepReasoning?.step_description,
    reason: stepReasoning?.reason,
    iconName: 'fin',
    type: 'step_reasoning',
    transaction_id: stepReasoning?.transaction_id,
    procedure_planner_run_id: stepReasoning?.procedure_planner_run_id,
    ...overrides,
  });

  // BASIC CASE: Single step reasoning
  if (!hasMultipleReasonedSteps) {
    let first = reasonedSteps[0];
    let stepNumber = first?.step_number ?? formatStepId(stepReasoning.step_number);
    let subProcedure = getSubProcedure(first?.section_name, stepReasoning.step_number);

    return baseEvent({
      i18nKey: subProcedure
        ? 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_sub_procedure'
        : 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning',
      sub_procedure: subProcedure || undefined,
      step_number: stepNumber,
    });
  }

  // Determine presence of main section & subproc
  let hasMainSection = reasonedSteps.some((s: ReasonedStep) => s.section_name === 'Main Section');
  let hasSubProcedure = reasonedSteps.some(
    (s: ReasonedStep) => getSubProcedure(s.section_name) !== undefined,
  );

  // COMPLEX CASE: Main section + sub procedure
  if (hasMainSection && hasSubProcedure) {
    let stepNumbers = reasonedSteps.map((step: ReasonedStep) =>
      step.section_name === 'Main Section'
        ? step.step_number
        : `${getSubProcedure(step.section_name)} ${step.step_number}`,
    );

    return baseEvent({
      i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_plural',
      step_number: joinStepNumbers(stepNumbers),
      sub_procedure: undefined,
    });
  }

  // Common step number formatting (main-only or subproc-only)
  let formattedNumbers = joinStepNumbers(reasonedSteps.map((s: ReasonedStep) => s.step_number));

  // STANDARD CASE: Multiple reasoned steps (main)
  if (hasMainSection) {
    return baseEvent({
      i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_plural',
      step_number: formattedNumbers,
      sub_procedure: undefined,
    });
  }

  // STANDARD CASE: Multiple reasoned steps (subproc)
  if (hasSubProcedure) {
    let firstSubProcedure = reasonedSteps.find(
      (s: ReasonedStep) => s.section_name !== 'Main Section',
    )?.section_name;

    return baseEvent({
      i18nKey:
        'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_sub_procedure_plural',
      step_number: formattedNumbers,
      sub_procedure: firstSubProcedure,
    });
  }

  return undefined;
};
